/* Presence policy (DOM-free, unit-tested): where collaborators' cursors are.
   Writes are the scarce thing (free Firestore tier), so a cursor is
   published at most every SEND_EVERY_MS and only when it moved; entries
   older than STALE_MS are ignored; nobody publishes while alone. */
export const SEND_EVERY_MS = 2000;
export const MIN_MOVE = 4;          // inches: smaller twitches aren't worth a write
export const STALE_MS = 45_000;
export const IDLE_MS = 60_000;      // stop publishing after this long without movement

export interface Cursor { x: number; y: number; name: string; color: string; ts: number }

/** Should a move at (x,y) at time `now` be published, given the last published one? */
export function shouldSend(last: { x: number; y: number; at: number } | null, x: number, y: number, now: number): boolean {
  if (!last) return true;
  if (now - last.at < SEND_EVERY_MS) return false;
  return Math.hypot(x - last.x, y - last.y) >= MIN_MOVE;
}
export const isFresh = (c: { ts: number }, now: number): boolean => now - c.ts < STALE_MS;

/** Everyone else who is still here, freshest first. */
export function others(all: Map<string, Cursor>, me: string, now: number): (Cursor & { uid: string })[] {
  return [...all.entries()].filter(([uid, c]) => uid !== me && isFresh(c, now))
    .map(([uid, c]) => ({ uid, ...c })).sort((a, b) => b.ts - a.ts);
}

const PALETTE = ['#E4572E', '#2E86AB', '#8E44AD', '#1B998B', '#C2185B', '#F18F01', '#3A86FF', '#6A994E'];
/** A stable colour per person. */
export function colorFor(uid: string): string {
  let h = 0;
  for (const ch of uid) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
/** "Gabriel Saunders" → "GS"; "bob@example.com" → "BO". */
export function initials(name: string): string {
  const w = name.split('@')[0].trim().split(/[\s._-]+/).filter(Boolean);
  return (w.length >= 2 ? w[0][0] + w[1][0] : (w[0] || '?').slice(0, 2)).toUpperCase();
}
